import { ref, onMounted, onUnmounted } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'

const BACKGROUND_RELOAD_THRESHOLD_MS = 5 * 60 * 1000

/**
 * PWA auto-update controller.
 *
 * vite-plugin-pwa's autoUpdate registerType downloads new SW versions silently,
 * but reload timing is controlled here to avoid wiping in-progress user input.
 * Reload triggers (any one of):
 *   - mount: the SW was already waiting from a previous session
 *   - app returns from background after >5 min
 *   - 'quote:saved' window event fires (safe action boundary)
 */
export function usePwaUpdate() {
  const { needRefresh, updateServiceWorker } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl) {
      console.log('[PWA] Service worker registered:', swUrl)
    },
    onRegisterError(error) {
      console.error('[PWA] Service worker registration failed:', error)
    }
  })

  const lastHiddenAt = ref<number | null>(null)

  function applyUpdate() {
    console.log('[PWA] Applying update — reloading')
    updateServiceWorker(true)
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      lastHiddenAt.value = Date.now()
      return
    }
    if (!needRefresh.value) return
    if (lastHiddenAt.value === null) return
    const hiddenFor = Date.now() - lastHiddenAt.value
    if (hiddenFor > BACKGROUND_RELOAD_THRESHOLD_MS) {
      applyUpdate()
    }
  }

  function onQuoteSaved() {
    if (needRefresh.value) applyUpdate()
  }

  onMounted(() => {
    if (needRefresh.value) {
      applyUpdate()
      return
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('quote:saved', onQuoteSaved)
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('quote:saved', onQuoteSaved)
  })

  return {
    needRefresh,
    applyUpdate
  }
}

export default usePwaUpdate
